import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router";
import { getQuizResults } from "../../services/quizService";
import { getUserQuizSession } from "../../services/quizSessionService";
import useNextQuiz from "../../hooks/quiz/useNextQuiz";
import BackLink from "../../components/ui/BackLink";
import { Helmet } from "react-helmet-async";
import { usePremiumModal } from "../../components/premium-modal/PremiumModalProvider";
import { CheckCircle, Play, XCircle, Sparkles, LockKeyhole } from "lucide-react";
import ProctoringReportSection from "./components/proctoring/ProctoringReportSection";
import NextQuizCard from "./components/NextQuizCard";
import ConfirmStartQuizModal from "../../components/modals/ConfirmStartQuizModal";
import { useAIQuizUsage } from "../../contexts/AIQuizUsageContext";
import { aiQuizService } from "../../services/aiQuizService";

const QuizResultsPage = () => {
  const { session_id: sessionId } = useParams();
  const { showPremiumModal, showUpgradeModal } = usePremiumModal();
  const { usage } = useAIQuizUsage();
  const retakesAllowed = usage?.features?.allow_retakes !== false;
  const navigate = useNavigate();
  const location = useLocation();

  const [isLimited, setIsLimited] = useState(false);
  const [limitMessage, setLimitMessage] = useState(null);

  const [results, setResults] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiReview, setAIReview] = useState(null);

  const [showConfirmStart, setShowConfirmStart] = useState(false);
  const [quizToStart, setQuizToStart] = useState(null);

  // Topic ID resolution for normal results or exam quizzes where result is not yet available
  const [sessionTopicId, setSessionTopicId] = useState(
    location.state?.topic_id || null
  );

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const data = await getQuizResults(sessionId);

        // Handle the case where results are not available yet
        if (data?.status === "result_not_available") {
          setError({
            message: data.message,
            data: data.data,
            type: "result_not_available",
          });
          if (data.data?.topic_id) {
            setSessionTopicId(data.data.topic_id);
          }
          return;
        }

        // Set the questions and session data
        setResults(data.results || []);
        setSessionData(data.session_data);
        aiQuizService.getQuizReview(sessionId).then(setAIReview).catch((reviewError) => {
          const reviewErrorData = reviewError?.response?.data;
          if (reviewErrorData?.recommendation_status || reviewErrorData?.ai_recommendations || reviewErrorData?.code === 'FREE_RECOMMENDATIONS_EXHAUSTED') {
            setAIReview({
              ...reviewErrorData,
              recommendation_status: reviewErrorData.recommendation_status || { code: reviewErrorData.code },
            });
          }
        });
        if (data.session_data?.topic_id) {
          setSessionTopicId(data.session_data.topic_id);
        }

        // Freemium restriction
        if (data?.is_limited) {
          setIsLimited(true);
          setLimitMessage(data.message);
        }
      } catch (error) {
        let errorObj = error?.response?.data;
        setError(errorObj);

        if (errorObj?.data?.topic_id) {
          setSessionTopicId(errorObj.data.topic_id);
        }

        if (error.status == 429) {
          showPremiumModal(
            errorObj?.message ||
            "You have reached the limit for free quiz attempts. Please upgrade to premium for unlimited access.",
          );
        }
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [sessionId]);

  // Sync sessionTopicId from results or state
  useEffect(() => {
    const tid =
      sessionData?.topic_id ||
      results?.[0]?.topic_detail?.id ||
      error?.data?.topic_id ||
      location.state?.topic_id;
    if (tid && tid !== sessionTopicId) {
      setSessionTopicId(tid);
    }
  }, [sessionData, results, error, location.state]);

  // Fallback: If sessionTopicId is still missing (e.g. direct URL visit on exam result_not_available), fetch session list
  useEffect(() => {
    if (sessionTopicId || !sessionId) return;

    let isMounted = true;
    const fetchSessionTopic = async () => {
      try {
        const sessionsData = await getUserQuizSession();
        if (!isMounted) return;
        const sessionsList = Array.isArray(sessionsData)
          ? sessionsData
          : sessionsData?.results || [];
        const currentSession = sessionsList.find((s) => s.id === sessionId);
        if (currentSession?.topic_detail?.id) {
          setSessionTopicId(currentSession.topic_detail.id);
        }
      } catch (err) {
        console.warn("Could not find session topic fallback:", err);
      }
    };

    fetchSessionTopic();
    return () => {
      isMounted = false;
    };
  }, [sessionTopicId, sessionId]);

  // Use custom hook to fetch the next quiz in progression
  const { nextQuiz, loading: loadingNextQuiz } = useNextQuiz(sessionTopicId);

  const handleStartNextQuiz = (quiz) => {
    setQuizToStart(quiz || nextQuiz);
    setShowConfirmStart(true);
  };



  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="loading loading-spinner loading-lg text-primary"></div>
      </div>
    );
  }

  if (error) {
    const isNotReady = error.type === "result_not_available";

    let checkBackDate = null;
    const match = error.message?.match(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\+\d{2}:\d{2})/);

    if (match) {
      const utcDate = new Date(match[1]);
      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
        timeZone: "Africa/Lagos",
      };
      checkBackDate = utcDate.toLocaleString("en-US", options) + " (WAT)";
    }

    return (
      <div>
        <BackLink />
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-6">
            {isNotReady ? "Test Submitted Successfully" : "Results Not Found"}
          </h1>

          {isNotReady && error.data ? (
            <>
              <p className="text-gray-600 mb-4">Thank you for completing the exam quiz.</p>

              <div className="stats shadow">
                <div className="stat">
                  <div className="stat-title">Score</div>
                  <div
                    className={`stat-value ${error.data.score / error.data.total_questions >= 0.5
                      ? "text-success"
                      : "text-error"
                      }`}
                  >
                    {Math.round((error.data.score / error.data.total_questions) * 100)}%
                  </div>
                </div>
                <div className="stat">
                  <div className="stat-title">Correct Answers</div>
                  <div className="stat-value text-primary">
                    {error.data.score}/{error.data.total_questions}
                  </div>
                </div>
              </div>

              <p className="text-gray-600 mb-2 mt-4">
                The detailed explanations and breakdown of your answers will be available once the
                quiz window closes.
              </p>
              {checkBackDate && (
                <p className="text-gray-600">
                  You can check back on: <strong>{checkBackDate}</strong> to view your full results.
                </p>
              )}

              {nextQuiz && (
                <div className="mt-8 text-left max-w-2xl mx-auto">
                  <NextQuizCard
                    quiz={nextQuiz}
                    onStartQuiz={handleStartNextQuiz}
                    loading={loadingNextQuiz}
                  />
                </div>
              )}

              <div className="mt-8 mx-auto">
                <iframe
                  src="https://docs.google.com/forms/d/e/1FAIpQLSePipxu3TsU-TrBeAC1FoocJ_C6lgPllcISGutEe_ZAow3I_Q/viewform?embedded=true"
                  width="700"
                  height="520"
                  frameBorder="0"
                  marginHeight="0"
                  marginWidth="0"
                  className="mx-auto w-full"
                >
                  Loading…
                </iframe>
              </div>
            </>
          ) : (
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">
              {error.message || error.detail || "Something went wrong. Please try again later."}
            </p>
          )}
        </div>

        <ConfirmStartQuizModal
          quiz={quizToStart || nextQuiz}
          isOpen={showConfirmStart}
          onClose={() => {
            setShowConfirmStart(false);
            setQuizToStart(null);
          }}
        />
      </div>
    );
  }

  // Generate quiz stats from session data
  const quizStats = sessionData ? [
    {
      title: "Score",
      value: `${sessionData.score_percentage}%`,
      color: sessionData.status === 'pass' ? "text-green-600" : "text-red-600",
    },
    {
      title: "Correct",
      value: sessionData.correct_answers.toString(),
      color: "text-green-600",
    },
    {
      title: "Wrong",
      value: sessionData.wrong_answers.toString(),
      color: "text-red-600",
    },
    {
      title: "Time",
      value: sessionData.time_taken?.formatted || "—",
      color: "text-primary",
    },
  ] : [];


  let pageTitle = "Quiz Results";
  if (sessionData?.quiz_title) {
    pageTitle = `${sessionData.quiz_title} Results`;
  }

  const topic_id = sessionTopicId || sessionData?.topic_id;

  return (
    <>
      <Helmet>
        <title>{pageTitle} | ExamPrep</title>
      </Helmet>
      <div>
        <BackLink text="Back to Results" redirectUrl={'/student/results'} />
        <div className="mb-8">
          <h1 className="text-xl font-medium mb-2">
            {results?.[0]?.topic_detail?.course_detail?.title || "Quiz Results"}
          </h1>
          <p className="opacity-66">
            {results?.[0]?.topic_detail?.course_detail?.code
              ? `${results[0].topic_detail.course_detail.code} — ${results[0].topic_detail?.title || ''}`
              : 'Course Quiz Results'}
          </p>

          {sessionData && (
            <div className="mt-5 grid sm:grid-cols-2 md:grid-cols-4 gap-4">
              {quizStats.map((stat, index) => (
                <div key={index} className="border border-gray-300 rounded-lg p-6 text-center">
                  <p className="text-sm opacity-66">{stat.title}</p>
                  <p className={`text-lg font-semibold ${stat.color}`}>{stat.value}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Proctoring Report Section */}
        <ProctoringReportSection sessionId={sessionId} />

        {/* Next Quiz Section */}
        {nextQuiz && (
          <div className="mb-8">
            <NextQuizCard
              quiz={nextQuiz}
              currentTopicTitle={results?.[0]?.topic_detail?.title || sessionData?.quiz_title}
              onStartQuiz={handleStartNextQuiz}
              loading={loadingNextQuiz}
            />
          </div>
        )}

        <div className="mb-5 flex flex-wrap gap-3 justify-between items-center">
          <p className="text-lg font-medium">Question Review</p>

          <div className="flex flex-wrap items-center gap-3">
            {topic_id && (
              <button
                className="btn btn-outline"
                onClick={() => {
                  if (!retakesAllowed) {
                    showUpgradeModal('retakes');
                    return;
                  }
                  navigate(`/student/explore-courses/quizzes/${topic_id}`, {
                    state: {
                      quiz_id: sessionData.quiz_id,
                      isRetakeFlow: true,
                    },
                  });
                }}
              >
                <Play className="w-4 h-4" /> Retake Quiz {!retakesAllowed && <span className="badge badge-warning badge-sm">Premium</span>}
              </button>
            )}

            {nextQuiz && (
              <button
                className="btn btn-primary"
                onClick={() => handleStartNextQuiz(nextQuiz)}
              >
                <Play className="w-4 h-4 fill-current" /> Take Next Quiz
              </button>
            )}
          </div>
        </div>

        {aiReview?.ai_recommendations ? (
          <div className="mb-6 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-base-100 to-indigo-500/10 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2 text-primary font-semibold"><Sparkles className="h-5 w-5" /> AI Study Recommendations</div>
            <div className="text-sm text-base-content/80 whitespace-pre-wrap">{typeof aiReview.ai_recommendations === 'string' ? aiReview.ai_recommendations : aiReview.ai_recommendations.summary || aiReview.ai_recommendations.content || JSON.stringify(aiReview.ai_recommendations)}</div>
          </div>
        ) : aiReview?.recommendation_status?.code === 'FREE_RECOMMENDATIONS_EXHAUSTED' ? (
          <div className="mb-6 rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-base-100 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2 text-amber-800 font-semibold"><LockKeyhole className="h-5 w-5" /> Premium AI Recommendations</div>
            <p className="text-sm text-base-content/80">You have used all 5 free AI recommendations. Upgrade to Premium to get continuous personalized study plans and weak-topic recommendations on every quiz.</p>
            <button className="btn btn-primary btn-sm mt-4" onClick={() => showUpgradeModal('recommendations', 'You have used all 5 free AI recommendations. Upgrade to Premium to get continuous personalized study plans and weak-topic recommendations on every quiz.')}>Upgrade to Premium</button>
          </div>
        ) : null}

        {isLimited && (
          <div className="mb-6 p-5 rounded-xl border border-yellow-300 bg-yellow-50">
            <h2 className="text-lg font-semibold mb-2">
              Explanation Limit Reached
            </h2>
            <p className="text-sm mb-4">
              {limitMessage}
            </p>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => showPremiumModal()}
            >
              Upgrade to Premium
            </button>
          </div>
        )}

        {!isLimited && (
          <div className="space-y-6">
            {results?.map((result, index) => {
              const options = result.options || [];
              const selectedOption = result.user_answer?.selected_option;
              const isCorrect = result.is_correct;

              const cardBgColor = isCorrect
                ? "bg-green-50 border-green-200"
                : "bg-red-50 border-red-200";

              return (
                <div key={result.id} className={`card shadow-sm border-2 ${cardBgColor}`}>
                  <div className="card-body">
                    <div className="flex justify-between items-start">
                      <p className="text-lg flex items-center gap-2 mb-3">
                        <span className={`${isCorrect ? "text-green-600" : "text-red-600"}`}>
                          {isCorrect ? <CheckCircle /> : <XCircle />}
                        </span>
                        Question {index + 1}: {result.text}
                      </p>
                    </div>

                    {result.image && (
                      <img
                        src={result.image}
                        alt="Question"
                        className="max-h-40 mb-4 rounded w-fit mx-auto"
                      />
                    )}

                    {/* OBJECTIVE */}
                    {result.question_type === "objective" && (
                      <div className="space-y-2">
                        {options.map((option) => {
                          const isSelected = selectedOption && option.id === selectedOption.id;
                          const showExplanation = isSelected && option.explanation;
                          const explanationColor = result.is_correct
                            ? "bg-green-100 border-green-300 text-green-900"
                            : "bg-red-100 border-red-300 text-red-900";
                          return (
                            <div
                              key={option.id}
                              className={`p-3 rounded-lg border ${option.is_correct
                                ? "bg-green-100 border-green-400"
                                : isSelected
                                  ? "bg-red-100 border-red-400"
                                  : "bg-white border-gray-300"
                                }`}
                            >
                              <div className="flex items-center gap-3">
                                {option.is_correct && (
                                  <span className="text-green-600">
                                    <CheckCircle className="w-5 h-5" />
                                  </span>
                                )}
                                {!option.is_correct && isSelected && (
                                  <span className="text-red-600">
                                    <XCircle className="w-5 h-5" />
                                  </span>
                                )}
                                <span className="flex-1">{option.text}</span>
                              </div>
                              {showExplanation && (
                                <div className={`mt-2 p-2 rounded border ${explanationColor}`}>
                                  <strong>Explanation:</strong> {option.explanation}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* MULTIPLE SELECT */}
                    {result.question_type === "multiple_select" && (
                      <div className="space-y-2">
                        {options.map((option) => {
                          const isSelected = (result.user_answer?.selected_options || []).some(
                            (opt) => opt.id === option.id,
                          );
                          const showExplanation = isSelected && option.explanation;
                          const explanationColor = result.is_correct
                            ? "bg-green-100 border-green-300 text-green-900"
                            : "bg-red-100 border-red-300 text-red-900";
                          return (
                            <div
                              key={option.id}
                              className={`p-3 rounded-lg border flex flex-col ${option.is_correct
                                ? "bg-green-100 border-green-400"
                                : isSelected
                                  ? "bg-red-100 border-red-400"
                                  : "bg-white border-gray-300"
                                }`}
                            >
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  readOnly
                                  className="checkbox checkbox-sm mr-2"
                                />
                                <span className="flex-1">{option.text}</span>
                                {option.is_correct && (
                                  <span className="text-green-600 ml-2">
                                    <CheckCircle className="w-4 h-4" />
                                  </span>
                                )}
                                {!option.is_correct && isSelected && (
                                  <span className="text-red-600 ml-2">
                                    <XCircle className="w-4 h-4" />
                                  </span>
                                )}
                              </div>
                              {showExplanation && (
                                <div className={`mt-2 p-2 rounded border ${explanationColor}`}>
                                  <strong>Explanation:</strong> {option.explanation}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* THEORY */}
                    {result.question_type === "theory" && (
                      <div className="space-y-2">
                        <div className="p-3 rounded-lg border border-gray-300 bg-white">
                          <strong>Your Answer:</strong>
                          <div className="mt-1">
                            {result.user_answer?.theory_answer || (
                              <span className="italic text-gray-400">No answer</span>
                            )}
                          </div>
                        </div>
                        <div className="p-3 rounded-lg border border-green-400 bg-green-100">
                          <strong>Correct Answer:</strong>
                          <div className="mt-1">{result.correct_answer}</div>
                        </div>
                      </div>
                    )}

                    {/* MATCHING */}
                    {result.question_type === "matching" && (
                      <div className="space-y-3">
                        <div className="overflow-x-auto">
                          <table className="table w-full bg-white">
                            <thead>
                              <tr>
                                <th className="bg-gray-100">Item</th>
                                <th className="bg-gray-100">Your Match</th>
                                <th className="bg-gray-100">Correct Match</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(result.matching_pair || []).map((pair) => {
                                const userMatch = (result.user_answer?.matching_answers || []).find(
                                  (ans) => ans.matching_pair === pair.id,
                                );
                                const isCorrect =
                                  userMatch && userMatch.selected_right === pair.right;
                                return (
                                  <tr
                                    key={pair.id}
                                    className={isCorrect ? "bg-green-100" : "bg-red-100"}
                                  >
                                    <td className="font-medium">{pair.left}</td>
                                    <td>
                                      {userMatch ? (
                                        <span
                                          className={isCorrect ? "text-green-600" : "text-red-600"}
                                        >
                                          {userMatch.selected_right}
                                          {isCorrect ? " ✓" : " ✗"}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400">No answer</span>
                                      )}
                                    </td>
                                    <td>
                                      <span className="text-green-600">{pair.right}</span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom Next Quiz Section */}
        {nextQuiz && (
          <div className="mt-8 p-5 rounded-xl bg-white border border-gray-300 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shadow-sm hover:border-gray-400 transition-all">
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-primary mb-1">
                Up Next
              </p>
              <h3 className="text-base sm:text-lg font-semibold text-base-content">
                Ready for the next quiz?
              </h3>
              <p className="text-sm opacity-70">
                Continue with <span className="font-medium opacity-100">{nextQuiz.topic_title} — {nextQuiz.quiz_type?.toUpperCase()} Quiz</span> ({nextQuiz.number_of_questions_to_answer} questions • {nextQuiz.expected_duration} mins)
              </p>
            </div>
            <button
              className="btn btn-primary gap-2 whitespace-nowrap self-end sm:self-center"
              onClick={() => handleStartNextQuiz(nextQuiz)}
            >
              <Play className="w-4 h-4 fill-current" /> Start Next Quiz
            </button>
          </div>
        )}

        {/* Modal for starting the next quiz */}
        <ConfirmStartQuizModal
          quiz={quizToStart || nextQuiz}
          isOpen={showConfirmStart}
          onClose={() => {
            setShowConfirmStart(false);
            setQuizToStart(null);
          }}
        />
      </div>
    </>
  );
};

export default QuizResultsPage;
